<template>
  <!-- 楼层悬挂 -->
  <nav class="question-nav" :aria-label="ariaLabel">
    <div ref="listRef" class="question-nav__list">
      <button
        v-for="item in items"
        :key="item.id"
        :ref="element => setItemElement(item.id, element)"
        type="button"
        class="question-nav__item"
        :class="{ 'is-active': item.id === activeId }"
        :aria-current="item.id === activeId ? 'location' : undefined"
        :aria-label="item.label"
        @click="$emit('select', item.id)"
      >
        <span class="question-nav__label">{{ item.label }}</span>
        <span class="question-nav__mark" aria-hidden="true"></span>
      </button>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import type { ComponentPublicInstance } from 'vue';

export interface ConversationQuestionNavItem {
  id: string;
  label: string;
}

const props = withDefaults(defineProps<{
  items: ConversationQuestionNavItem[];
  activeId?: string;
  ariaLabel?: string;
}>(), {
  activeId: '',
  ariaLabel: 'Conversation questions',
});

defineEmits<{
  select: [id: string];
}>();

const listRef = ref<HTMLElement | null>(null);
const itemElements = new Map<string, HTMLButtonElement>();

const setItemElement = (
  id: string,
  element: Element | ComponentPublicInstance | null,
) => {
  if (element instanceof HTMLButtonElement) {
    itemElements.set(id, element);
  } else {
    itemElements.delete(id);
  }
};

// 新问题成为当前轮次时，只滚动导航列表，确保绿色项进入六条可视区域。
const revealActiveItem = async () => {
  await nextTick();
  const list = listRef.value;
  const item = itemElements.get(props.activeId);
  if (!list || !item) return;

  const listRect = list.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();

  if (itemRect.top < listRect.top) {
    list.scrollTo({ top: list.scrollTop + itemRect.top - listRect.top, behavior: 'smooth' });
  } else if (itemRect.bottom > listRect.bottom) {
    list.scrollTo({ top: list.scrollTop + itemRect.bottom - listRect.bottom, behavior: 'smooth' });
  }
};

watch(
  [() => props.activeId, () => props.items.length],
  revealActiveItem,
  { flush: 'post', immediate: true },
);
</script>

<style scoped lang="scss">
.question-nav {
  position: fixed;
  top: 50%;
  right: clamp(18px, 2.5vw, 48px);
  z-index: 20;
  box-sizing: border-box;
  width: 34px;
  max-height: min(230px, 60vh);
  padding: 10px 7px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: transparent;
  box-shadow: none;
  transform: translateY(-50%);
  transition:
    width .22s ease,
    padding .22s ease,
    border-color .22s ease,
    background-color .22s ease,
    box-shadow .22s ease;

  &:hover,
  &:focus-within {
    width: 250px;
    padding: 10px 8px;
    border-color: rgba(0, 0, 0, .035);
    background: rgba(255, 255, 255, .96);
    box-shadow: 0 5px 18px rgba(0, 0, 0, .1);
    backdrop-filter: blur(8px);

    .question-nav__item {
      gap: 8px;
      justify-content: flex-start;

      &.is-active { background: rgba(88, 189, 110, .13); }
    }

    .question-nav__label {
      flex: 1 1 auto;
      width: auto;
      opacity: 1;
      visibility: visible;
      transition-delay: .08s;
    }

    .question-nav__list {
      scrollbar-width: thin;

      &::-webkit-scrollbar { width: 6px; }
    }
  }
}

.question-nav__list {
  max-height: min(210px, calc(60vh - 20px));
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-color: #c9c9c9 transparent;
  scrollbar-width: none;

  &::-webkit-scrollbar { width: 0; }
}

.question-nav__item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  width: 100%;
  min-height: 35px;
  min-width: 0;
  padding: 6px 4px;
  border: 0;
  color: #737373;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: color .2s ease, background-color .2s ease;

  &:hover,
  &:focus-visible {
    color: #444;
    background: #f7f7f7;
    outline: none;
  }

  &.is-active {
    color: var(--green);

    .question-nav__mark { background: var(--green); }
  }

  // 悬浮后在当前横格内展开完整问题，避免提示层被滚动区域裁切。
  &:hover .question-nav__label,
  &:focus-visible .question-nav__label {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
  }
}

.question-nav__label {
  min-width: 0;
  flex: 0 0 0;
  width: 0;
  overflow: hidden;
  font-size: 16px;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow-wrap: anywhere;
  opacity: 0;
  visibility: hidden;
  transition: opacity .12s ease;
}

.question-nav__mark {
  flex: 0 0 auto;
  width: 11px;
  height: 3px;
  border-radius: 999px;
  background: #d1d1d1;
  transition: background-color .2s ease;
}

@media (max-width: 900px) {
  .question-nav { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .question-nav,
  .question-nav__label { transition: none; }
}
</style>
